import { StageDef } from '../automationMachineInterfaces'
import { REDDIT_AUTOMATION_STAGE_STATUSES } from './redditAutomationStageStatuses'
import * as redditUrls from './redditUrls'
import { getSwitchValue, toggleSwitch } from './switchUtils'
import { RedditSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

type EmailNotificationsSetting = {
  key: RedditSettingKey
  section: string
  label: string
}

const SETTINGS_SECTION = 'settings-emails'
const UNSUBSCRIBE_ALL_LABEL = 'Unsubscribe from all emails'

const settings: EmailNotificationsSetting[] = [
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_CHAT_REQUESTS,
    label: 'Chat requests',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_COMMENTS,
    label: 'Comments on your posts',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_REPLIES,
    label: 'Replies to your comments',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_UPVOTES,
    label: 'Upvotes on your posts',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_COMMENT_UPVOTES,
    label: 'Upvotes on your comments',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_MENTIONS,
    label: 'Username mentions',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_FOLLOWERS,
    label: 'New followers',
    section: SETTINGS_SECTION,
  },
  {
    key: RedditSettingKey.EMAIL_NOTIFICATION_DAILY_DIGEST,
    label: 'Daily Digest',
    section: SETTINGS_SECTION,
  },
]

const getCurrentValueFn = (key: string) =>
  getCurrentValueFnFromKey(key, (value) => (value === true ? 'On' : 'Off'))

const getIsFulfilledFn = (key: string) =>
  getIsFulfilledFnFromKey(key, (value) => value === false)

function getCorrectValue(): boolean {
  return false
}

function isUnsubscribedAll(): Promise<boolean> {
  return getSwitchValue(
    SETTINGS_SECTION,
    UNSUBSCRIBE_ALL_LABEL,
    UNSUBSCRIBE_ALL_LABEL,
  )
}

const gatherStageDefs: StageDef[] = settings.map((s) => ({
  type: enums.StageType.ACTION,
  key: `GATHER_REDDIT_SETTING_${s.key}`,
  status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
  url: redditUrls.emailsSettingsUrl,
  action: async (_machineContext, userContext) => {
    if (await isUnsubscribedAll()) {
      await userContext.updateStoredInitialValues(
        { [s.key]: false },
        enums.RedditRecommendationKey.REDDIT_EMAIL_NOTIFICATIONS,
      )
      return
    }

    const value = await getSwitchValue(s.section, s.label, s.label)
    await userContext.updateStoredInitialValues(
      {
        [s.key]: value,
      },
      enums.RedditRecommendationKey.REDDIT_EMAIL_NOTIFICATIONS,
    )
  },
}))

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UPDATE_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: redditUrls.emailsSettingsUrl,
    action: async (machineContext, userContext) => {
      if (await isUnsubscribedAll()) {
        return
      }

      if (
        !userContext.settingsSelected ||
        !userContext.settingsSelected[s.key]
      ) {
        return
      }
      const currentValue = await getSwitchValue(s.section, s.label, s.label)
      const correctValue = getCorrectValue()
      if (!userContext.isDry && currentValue !== correctValue) {
        await toggleSwitch(s.section, s.label, s.label)
      }
    },
  })),
]

const undoStageDefs: StageDef[] = [
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UNDO_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: redditUrls.emailsSettingsUrl,
    action: async (machineContext, userContext) => {
      if (await isUnsubscribedAll()) {
        throw new Error(
          `${s.key}: Cannot undo when "unsubscribe all" is toggled`,
        )
      }

      if (!userContext.initialValues || !userContext.initialValues[s.key]) {
        return
      }

      const currentValue = await getSwitchValue(s.section, s.label, s.label)
      if (
        !userContext.isDry &&
        currentValue !== userContext.initialValues[s.key]
      ) {
        await toggleSwitch(s.section, s.label, s.label)
      }
    },
  })),
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
